import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { billingApi } from '@/api/ops';
import type { GatewayTestResult, PaymentGateway } from '@/api/opsTypes';
import { PERMISSIONS } from '@/api/types';
import { hasPermission } from '@/auth/session';
import { Segmented, Select } from '@/components/Select';
import { ConfirmDialog, Modal } from './Modal';
import { MutationError } from './Problem';
import { StatusChip } from './Badges';
import { CheckboxField, ChoiceChips, ReasonField, TextAreaField, TextField, reasonOk, splitList, toggleIn } from './Forms';
import { formatTimestamp, shortId } from '@/ops/format';
import { TenantName } from '@/components/ops/TenantPicker';

/**
 * The Paymob config (CONTRACTS §4.6, billing `PaymobGateway.ValidateConfig`): these four are encrypted at
 * rest and write-only, and billing refuses a row that lacks any of them. The hints say where each value
 * lives in the Paymob dashboard so an admin can fill the form from it without a second document.
 */
export const PAYMOB_SECRETS = [
  { name: 'secret_key', label: 'Secret key', placeholder: 'egy_sk_test_… or egy_sk_live_…',
    hint: 'Paymob dashboard → Settings → Account Info → Secret Key. Authorises the checkout (intention) call.' },
  { name: 'public_key', label: 'Public key', placeholder: 'egy_pk_test_… or egy_pk_live_…',
    hint: 'Settings → Account Info → Public Key. Goes into the hosted checkout URL.' },
  { name: 'hmac_secret', label: 'HMAC secret', placeholder: '32 hex characters',
    hint: 'Settings → Account Info → HMAC Secret. Verifies every Paymob callback and return — a wrong value makes every payment look forged.' },
  { name: 'api_key', label: 'API key', placeholder: 'the long base64 value',
    hint: 'Settings → Account Info → API Key. Billing tests it before saving; it also serves payment status and refunds.' },
] as const;

const METHODS = ['card', 'wallet', 'kiosk'];
const PAYMOB_METHOD_LABELS: Record<string, string> = { card: 'Online Card', wallet: 'Mobile Wallet', kiosk: 'Kiosk' };
export const PAYMOB_BASE_URL = 'https://accept.paymob.com';
const GATEWAY_KEYS = [
  { value: 'paymob', label: 'Paymob (Egypt — cards, wallets, kiosks)' },
  { value: 'other', label: 'Another adapter key…', hint: 'unavailable until registered' },
];

export const gatewayQueryKey = (env: string) => ['ops', 'billing', env, 'gateways'] as const;

type IntegrationIds = Record<string, string>;
/** Billing's `config_public.checkout_flow` values (the Paymob adapter): the hosted Intention-API page, or the Accept iframe. */
type CheckoutFlow = 'intention' | 'accept_iframe';

/** `integration_ids` / `base_url` / `checkout_flow` / `iframe_id` as the form edits them, read from a stored row's public config. */
function readPaymobPublic(config: Record<string, unknown> | null | undefined): { ids: IntegrationIds; baseUrl: string; checkoutFlow: CheckoutFlow; iframeId: string } {
  const ids: IntegrationIds = { card: '', wallet: '', kiosk: '' };
  const raw = config?.integration_ids;
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    for (const method of METHODS) {
      const value = (raw as Record<string, unknown>)[method];
      if (typeof value === 'number' || typeof value === 'string') ids[method] = String(value);
    }
  }
  const baseUrl = typeof config?.base_url === 'string' && config.base_url.trim() ? config.base_url.trim() : PAYMOB_BASE_URL;
  const checkoutFlow: CheckoutFlow = config?.checkout_flow === 'accept_iframe' ? 'accept_iframe' : 'intention';
  const iframeId = typeof config?.iframe_id === 'number' || typeof config?.iframe_id === 'string' ? String(config.iframe_id) : '';
  return { ids, baseUrl, checkoutFlow, iframeId };
}

/**
 * A Paymob key names its own mode (`egy_sk_test_…` / `egy_sk_live_…`). Billing accepts any key under any
 * mode, so the only place a mix-up can be caught is before the save — said here, not blocking, because the
 * stored secret cannot be re-read to check later.
 */
export function paymobKeyModeMismatch(secrets: Record<string, string>, testMode: boolean): string | null {
  const keys = [secrets.secret_key, secrets.public_key].filter((v): v is string => !!v && v.trim() !== '');
  if (keys.some((k) => /_test_/i.test(k)) && !testMode) {
    return 'A Paymob TEST key (…_test_…) with Live selected: live payments through this row would fail. Choose Test mode, or paste the live keys.';
  }
  if (keys.some((k) => /_live_/i.test(k)) && testMode) {
    return 'A Paymob LIVE key (…_live_…) with Test mode selected: real cards would be charged through a row marked test. Choose Live, or paste the test keys.';
  }
  return null;
}

function httpsOrigin(value: string): string | null {
  try {
    const url = new URL(value.trim());
    if (url.protocol !== 'https:' || url.search || url.username) return null;
    return `${url.origin}${url.pathname}`.replace(/\/+$/, '');
  } catch {
    return null;
  }
}

/**
 * Create / edit one `payment_gateways` row. Secrets are WRITE-ONLY (BL5): the inputs start empty, are
 * password fields, and an empty secret on an edit keeps the stored value. Nothing typed here is kept
 * after the dialog closes.
 *
 * For `paymob` the form is typed — the four secrets with their dashboard locations, one integration ID
 * per method, the base URL, and ONE Test/Live choice that writes both the row's `test_mode` and the
 * config's `mode` (they used to be two separate inputs that could disagree). Any other key keeps the
 * raw JSON config, since no other adapter is registered yet (R10: such a row shows as unavailable).
 */
export function GatewayForm({
  env, gateway, ownerTenantId, onClose,
}: {
  env: string;
  /** Edit this row; absent = create. */
  gateway?: PaymentGateway;
  /** Create a BYO row owned by this tenant (null/undefined = a platform row). */
  ownerTenantId?: string | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const editing = !!gateway;
  const [keyChoice, setKeyChoice] = useState<'paymob' | 'other'>(!gateway || gateway.gateway_key === 'paymob' ? 'paymob' : 'other');
  const [customKey, setCustomKey] = useState(gateway && gateway.gateway_key !== 'paymob' ? gateway.gateway_key : '');
  const gatewayKey = keyChoice === 'paymob' ? 'paymob' : customKey.trim();
  const paymob = gatewayKey === 'paymob';

  const [displayName, setDisplayName] = useState(gateway?.display_name ?? '');
  const [priority, setPriority] = useState(String(gateway?.priority ?? 100));
  const [currencies, setCurrencies] = useState((gateway?.currencies ?? ['EGP']).join(', '));
  const [otherMethods, setOtherMethods] = useState<string[]>(gateway?.methods ?? ['card']);
  const [testMode, setTestMode] = useState(gateway?.test_mode ?? true);
  const [enabled, setEnabled] = useState(false);
  const [secrets, setSecrets] = useState<Record<string, string>>({});
  const [stored] = useState(() => readPaymobPublic(gateway?.config_public));
  const [integrationIds, setIntegrationIds] = useState<IntegrationIds>(stored.ids);
  const [baseUrl, setBaseUrl] = useState(stored.baseUrl);
  const [checkoutFlow, setCheckoutFlow] = useState<CheckoutFlow>(stored.checkoutFlow);
  const [iframeId, setIframeId] = useState(stored.iframeId);
  const [configJson, setConfigJson] = useState(JSON.stringify(gateway?.config_public ?? {}, null, 2));
  const [reason, setReason] = useState('');

  // ---- any other key: a raw JSON object ----
  let configError: string | null = null;
  let rawConfig: unknown = null;
  if (!paymob) {
    try {
      rawConfig = configJson.trim() ? JSON.parse(configJson) : null;
      if (rawConfig !== null && (typeof rawConfig !== 'object' || Array.isArray(rawConfig))) {
        configError = 'Public config must be a JSON object.';
      }
    } catch {
      configError = 'Public config is not valid JSON.';
    }
  }

  // ---- Paymob: typed fields ----
  const idError = (method: string): string | null => {
    const value = integrationIds[method]?.trim() ?? '';
    if (value === '') return null;
    return /^[1-9]\d*$/.test(value) ? null : 'A positive whole number (the ID column of the integration).';
  };
  const filledMethods = METHODS.filter((m) => (integrationIds[m]?.trim() ?? '') !== '');
  const idFormatError = paymob ? METHODS.map(idError).find((e) => e) ?? null : null;
  const noIdsError = paymob && filledMethods.length === 0 ? 'At least one integration ID is needed (card, wallet or kiosk).' : null;
  const baseUrlError = paymob && httpsOrigin(baseUrl) === null ? 'An https origin, e.g. https://accept.paymob.com.' : null;
  const iframeError = !paymob ? null
    : iframeId.trim() !== '' && !/^[1-9]\d*$/.test(iframeId.trim()) ? 'A positive whole number (Developers → Iframes, the ID column).'
      : checkoutFlow === 'accept_iframe' && iframeId.trim() === '' ? 'The Accept iframe needs the iframe ID (Paymob dashboard → Developers → Iframes).'
        : null;
  const secretEntries = Object.entries(secrets).filter(([, v]) => v.trim() !== '');
  const missingSecrets = paymob && !editing ? PAYMOB_SECRETS.filter((s) => (secrets[s.name]?.trim() ?? '') === '') : [];
  const modeMismatch = paymob ? paymobKeyModeMismatch(secrets, testMode) : null;

  const currencyList = splitList(currencies).map((c) => c.toUpperCase());
  const currencyError = currencyList.some((c) => !/^[A-Z]{3}$/.test(c)) ? 'ISO-4217 codes, e.g. EGP, USD.' : null;
  const priorityNumber = Number(priority);
  const priorityError = !Number.isInteger(priorityNumber) || priorityNumber < 0 || priorityNumber > 100000 ? 'A whole number from 0 to 100000.' : null;

  const methods = paymob ? filledMethods : otherMethods;
  const valid =
    gatewayKey !== '' && displayName.trim() !== '' && !currencyError && !priorityError && reasonOk(reason)
    && !configError && !idFormatError && !noIdsError && !baseUrlError && !iframeError && missingSecrets.length === 0;

  const save = useMutation({
    mutationFn: () => {
      // Keys this form does not know (stored by billing or set through its admin API) survive an edit untouched; the
      // typed fields overwrite only their own keys. The default flow is not written (billing drops it anyway).
      const configPublic = paymob
        ? {
          ...(gateway?.config_public ?? {}),
          integration_ids: Object.fromEntries(filledMethods.map((m) => [m, Number((integrationIds[m] ?? '').trim())])),
          base_url: httpsOrigin(baseUrl) ?? PAYMOB_BASE_URL,
          mode: testMode ? 'test' : 'live',
          checkout_flow: checkoutFlow === 'accept_iframe' ? 'accept_iframe' : undefined,
          iframe_id: iframeId.trim() !== '' ? Number(iframeId.trim()) : undefined,
        }
        : rawConfig;
      const body: Record<string, unknown> = {
        display_name: displayName.trim(),
        priority: priorityNumber,
        currencies: currencyList,
        methods,
        test_mode: testMode,
        config_public: configPublic,
        reason: reason.trim(),
      };
      // Only the secrets actually typed are sent; the rest keep their stored (encrypted) values.
      if (secretEntries.length > 0) body.secrets = Object.fromEntries(secretEntries.map(([k, v]) => [k, v.trim()]));
      if (editing) return billingApi.updateGateway(env, gateway!.id, body);
      return billingApi.createGateway(env, {
        ...body,
        gateway_key: gatewayKey,
        owner_tenant_id: ownerTenantId ?? null,
        enabled,
      });
    },
    onSuccess: () => {
      setSecrets({});
      queryClient.invalidateQueries({ queryKey: gatewayQueryKey(env) });
      onClose();
    },
  });

  const secretNames: { name: string; label: string; placeholder: string; hint: string }[] = paymob
    ? [...PAYMOB_SECRETS]
    : Object.keys(gateway?.secrets ?? {}).map((name) => ({ name, label: name, placeholder: '', hint: '' }));

  return (
    <Modal
      title={editing ? `Edit gateway — ${gateway!.display_name}` : ownerTenantId ? 'Add a BYO gateway' : 'Add a platform gateway'}
      size="lg"
      onClose={save.isPending ? undefined : onClose}
      dismissible={!save.isPending}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={save.isPending}>Cancel</button>
          <button type="button" className="btn btn-primary" disabled={!valid || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : editing ? 'Save gateway' : 'Create gateway'}
          </button>
        </>
      }
    >
      {ownerTenantId && (
        <p className="t-caption muted" style={{ margin: 0 }}>
          A BYO row belongs to tenant <span className="mono">{ownerTenantId}</span>: only that account's external
          customer payments can use it, and it is verified with its own secret. Our own sales never use it.
        </p>
      )}
      <div className="form-grid">
        <Select label="Gateway" value={keyChoice} onChange={(v) => setKeyChoice(v === 'other' ? 'other' : 'paymob')}
          options={GATEWAY_KEYS} disabled={editing}
          hint={editing ? 'The adapter key cannot change on an existing row.' : 'The adapter that runs this row. Only Paymob is registered today.'} />
        {keyChoice === 'other' && (
          <TextField label="Adapter key" value={customKey} onChange={setCustomKey} disabled={editing} mono required
            hint="A key with no registered adapter saves, but the row shows as unavailable and never takes a payment." />
        )}
        <TextField label="Display name" value={displayName} onChange={setDisplayName} required
          hint="Shown in the console only, e.g. “Anyware Store — Paymob (test)”." />
        <TextField label="Priority" value={priority} onChange={setPriority} type="number" error={priorityError} hint="Lower runs first." />
        <TextField label="Currencies" value={currencies} onChange={setCurrencies} error={currencyError} hint="Comma separated. Paymob settles in EGP." />
      </div>

      <Segmented label="Mode" value={testMode ? 'test' : 'live'} onChange={(v) => setTestMode(v === 'test')}
        options={[{ value: 'test', label: 'Test mode' }, { value: 'live', label: 'Live' }]} />
      <p className="t-caption muted" style={{ margin: 0 }}>
        {paymob
          ? 'Sets the row’s test flag and Paymob’s mode together. For a test row paste the keys shown while the Paymob dashboard’s TEST switch is on (they contain “_test_”). A test row serves only sandbox API keys; a live row only live keys.'
          : 'A test row serves only sandbox API keys; a live row only live keys.'}
      </p>
      {modeMismatch && (
        <div className="callout callout-warning" role="status">
          <span className="callout-icon" aria-hidden="true">!</span>
          <div className="t-body">{modeMismatch}</div>
        </div>
      )}
      {!editing && (
        <CheckboxField label="Enable on create" checked={enabled} onChange={setEnabled}
          hint="Billing tests the credentials before saving either way. An enabled row is eligible for checkout at once; a disabled one can be enabled from the table." />
      )}

      {paymob ? (
        <>
          <h3 className="t-heading" style={{ margin: 0 }}>Payment integrations</h3>
          <p className="t-caption muted" style={{ margin: 0 }}>
            Paymob dashboard → Developers → Payment Integrations: copy the ID of each integration this row should offer.
            A filled ID switches that payment method on; leave the others empty.
          </p>
          <div className="form-grid">
            {METHODS.map((method) => (
              <TextField key={method} label={`Integration ID — ${PAYMOB_METHOD_LABELS[method]}`} value={integrationIds[method] ?? ''} mono
                placeholder={method === 'card' ? 'e.g. 1845281' : 'empty = not offered'}
                onChange={(value) => setIntegrationIds((prev) => ({ ...prev, [method]: value }))}
                error={idError(method)} />
            ))}
            <TextField label="Base URL" value={baseUrl} onChange={setBaseUrl} type="url" mono error={baseUrlError}
              hint="Paymob’s Accept API origin. Only change it for a Paymob region other than Egypt." />
          </div>
          {noIdsError && <span className="t-caption note-error">{noIdsError}</span>}

          <Segmented label="Checkout page" value={checkoutFlow} onChange={(v) => setCheckoutFlow(v === 'accept_iframe' ? 'accept_iframe' : 'intention')}
            options={[{ value: 'intention', label: 'Paymob hosted checkout' }, { value: 'accept_iframe', label: 'Accept iframe' }]} />
          <p className="t-caption muted" style={{ margin: 0 }}>
            {checkoutFlow === 'intention'
              ? 'The Intention API’s hosted page: callbacks are set per payment, so one integration can serve every row. Pick the Accept iframe only when Paymob answers “Integration ID does not exist” for this integration.'
              : 'The older Accept flow (order → payment key → iframe). Paymob sends its callbacks to the URLs saved on the integration itself, so the integration’s “Transaction processed / response callback” must point at this row’s webhook and return URLs, and one integration can serve one row.'}
          </p>
          {checkoutFlow === 'accept_iframe' && (
            <div className="form-grid">
              <TextField label="Iframe ID" value={iframeId} onChange={setIframeId} mono required placeholder="e.g. 355072" error={iframeError}
                hint="Paymob dashboard → Developers → Iframes: the ID of the card iframe this row opens." />
            </div>
          )}
        </>
      ) : (
        <>
          <ChoiceChips label="Methods" options={METHODS.map((m) => ({ value: m }))} selected={otherMethods} onToggle={(m) => setOtherMethods(toggleIn(otherMethods, m))} />
          <TextAreaField label="Public config (JSON)" value={configJson} onChange={setConfigJson} mono rows={6} error={configError}
            hint="Non-secret settings, as the adapter expects them." />
        </>
      )}

      {secretNames.length > 0 && (
        <>
          <h3 className="t-heading" style={{ margin: 0 }}>Secrets (write-only)</h3>
          <p className="t-caption muted" style={{ margin: 0 }}>
            {editing
              ? 'Leave a field empty to keep the stored value. Stored values are never shown — only whether one is set.'
              : paymob
                ? 'All four come from the Paymob dashboard (Settings → Account Info). Billing encrypts them before storing; the console never keeps or displays them.'
                : 'Encrypted by billing before it is stored. The console never keeps or displays them.'}
          </p>
          <div className="form-grid">
            {secretNames.map((secret) => (
              <TextField
                key={secret.name}
                label={`${secret.label}${gateway?.secrets?.[secret.name] ? ' (set)' : ''}`}
                type="password"
                mono
                required={paymob && !editing}
                value={secrets[secret.name] ?? ''}
                placeholder={gateway?.secrets?.[secret.name] ? '•••••• keep current' : secret.placeholder}
                hint={secret.hint || undefined}
                onChange={(value) => setSecrets((prev) => ({ ...prev, [secret.name]: value }))}
              />
            ))}
          </div>
          {missingSecrets.length > 0 && (
            <span className="t-caption note-error">
              Billing needs all four to create a Paymob row — still missing: {missingSecrets.map((s) => s.label).join(', ')}.
            </span>
          )}
        </>
      )}

      <ReasonField value={reason} onChange={setReason} />
      <MutationError error={save.error} service="billing" />
    </Modal>
  );
}

/**
 * The gateway rows with their actions. `status: unavailable` (no registered adapter, R10) is shown as
 * such — a checkout that would pick that row answers 422, never a 500.
 */
export function GatewaysTable({ env, gateways, showOwner = true }: { env: string; gateways: PaymentGateway[]; showOwner?: boolean }) {
  const queryClient = useQueryClient();
  const canManage = hasPermission(PERMISSIONS.billingManage);
  const [editing, setEditing] = useState<PaymentGateway | null>(null);
  const [action, setAction] = useState<{ gateway: PaymentGateway; kind: 'enable' | 'disable' | 'retire' } | null>(null);
  const [reason, setReason] = useState('');
  const [testResult, setTestResult] = useState<GatewayTestResult | null>(null);

  const act = useMutation({
    mutationFn: () => billingApi.gatewayAction(env, action!.gateway.id, action!.kind, reason.trim()),
    onSuccess: () => {
      setAction(null);
      setReason('');
      queryClient.invalidateQueries({ queryKey: gatewayQueryKey(env) });
    },
  });

  const test = useMutation({
    mutationFn: (id: string) => billingApi.testGateway(env, id),
    onSuccess: (result) => {
      setTestResult(result);
      queryClient.invalidateQueries({ queryKey: gatewayQueryKey(env) });
    },
  });

  if (gateways.length === 0) {
    return <div className="empty-state t-body">No gateway rows.</div>;
  }

  return (
    <>
      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>Gateway</th>
              {showOwner && <th>Scope</th>}
              <th>Status</th>
              <th className="num">Priority</th>
              <th>Currencies · methods</th>
              <th>Secrets</th>
              <th>Last verified</th>
              {canManage && <th aria-label="Actions" />}
            </tr>
          </thead>
          <tbody>
            {gateways.map((g) => (
              <tr key={g.id} className={g.retired ? 'row-muted' : undefined}>
                <td>
                  <div style={{ fontWeight: 600 }}>{g.display_name}</div>
                  <div className="t-caption muted mono">{g.gateway_key} · {shortId(g.id)}</div>
                  {g.webhook_url && <div className="t-caption subtle mono wrap" title="Webhook URL">{g.webhook_url}</div>}
                </td>
                {showOwner && (
                  <td>
                    {g.owner_tenant_id ? (
                      <>
                        <StatusChip status="byo" tone="info" />
                        <div className="t-caption"><TenantName env={env} id={g.owner_tenant_id} tab="integrations" /></div>
                      </>
                    ) : (
                      <StatusChip status="platform" tone="muted" />
                    )}
                  </td>
                )}
                <td>
                  <div className="row">
                    <StatusChip status={g.status} />
                    {g.retired ? <StatusChip status="retired" /> : <StatusChip status={g.enabled ? 'enabled' : 'disabled'} />}
                    {g.test_mode && <StatusChip status="test mode" tone="warning" />}
                  </div>
                  {!g.adapter_registered && (
                    <div className="t-caption note-error">No adapter registered for “{g.gateway_key}” — unavailable.</div>
                  )}
                </td>
                <td className="num">{g.priority}</td>
                <td className="t-caption">
                  {g.currencies.join(', ') || '—'}
                  <div className="muted">{g.methods.join(', ') || '—'}</div>
                </td>
                <td className="t-caption">
                  {Object.keys(g.secrets).length === 0
                    ? <span className="subtle">none</span>
                    : Object.entries(g.secrets).map(([name, set]) => (
                      <div key={name} className="nowrap" title={set ? 'set' : 'missing'}>
                        <span className="mono">{name}</span> {set ? '✓' : <span className="note-error">✕ missing</span>}
                      </div>
                    ))}
                </td>
                <td className="t-caption muted">{g.last_verified_at ? formatTimestamp(g.last_verified_at) : 'never'}</td>
                {canManage && (
                  <td>
                    <div className="row">
                      <button type="button" className="btn btn-ghost btn-sm" onClick={() => setEditing(g)} disabled={g.retired}>Edit</button>
                      <button type="button" className="btn btn-ghost btn-sm" disabled={test.isPending || g.retired}
                        onClick={() => test.mutate(g.id)}>
                        {test.isPending && test.variables === g.id ? 'Testing…' : 'Test'}
                      </button>
                      {!g.retired && (
                        <button type="button" className="btn btn-ghost btn-sm"
                          onClick={() => setAction({ gateway: g, kind: g.enabled ? 'disable' : 'enable' })}>
                          {g.enabled ? 'Disable' : 'Enable'}
                        </button>
                      )}
                      {!g.retired && (
                        <button type="button" className="btn btn-ghost btn-sm is-danger"
                          onClick={() => setAction({ gateway: g, kind: 'retire' })}>
                          Retire
                        </button>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <MutationError error={test.error} service="billing" />
      {testResult && (
        <div className={`callout ${testResult.ok ? 'callout-info' : 'callout-error'}`} role="status">
          <span className="callout-icon" aria-hidden="true">{testResult.ok ? 'i' : '✕'}</span>
          <div className="t-body">
            <strong className="t-heading" style={{ display: 'block' }}>
              Credential test {testResult.ok ? 'passed' : 'failed'} — {testResult.status}
            </strong>
            {testResult.detail ?? 'No detail returned.'}
            <button type="button" className="btn btn-ghost btn-sm" style={{ marginInlineStart: 8 }} onClick={() => setTestResult(null)}>Dismiss</button>
          </div>
        </div>
      )}

      {editing && <GatewayForm env={env} gateway={editing} onClose={() => setEditing(null)} />}

      {action && (
        <ConfirmDialog
          title={`${action.kind.charAt(0).toUpperCase()}${action.kind.slice(1)} ${action.gateway.display_name}?`}
          confirmLabel={action.kind === 'retire' ? 'Retire gateway' : action.kind === 'enable' ? 'Enable gateway' : 'Disable gateway'}
          destructive={action.kind !== 'enable'}
          pending={act.isPending}
          confirmDisabled={!reasonOk(reason)}
          onConfirm={() => act.mutate()}
          onCancel={() => {
            setAction(null);
            setReason('');
            act.reset();
          }}
        >
          <p className="t-body" style={{ margin: 0 }}>
            {action.kind === 'retire'
              ? 'A retired row takes no new payments and is no longer reconciled — a pending checkout whose webhook was lost never settles. Disable it first and retire once nothing is in flight. Retiring cannot be undone here.'
              : action.kind === 'disable'
                ? 'A disabled row is skipped by selection; webhooks and reconciliation for in-flight payments keep working.'
                : 'An enabled row becomes eligible for checkout selection by priority.'}
          </p>
          <ReasonField value={reason} onChange={setReason} />
          <MutationError error={act.error} service="billing" />
        </ConfirmDialog>
      )}
    </>
  );
}
